import { useRef, useState } from 'react'
import { Camera, Loader2, ScanLine } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'

// HINs are 12 chars (post-1984) or 14 chars (legacy), uppercase alphanumeric.
const HIN_TOKEN = /\b[A-Z0-9]{12,14}\b/
// OCR's usual letter/digit confusions; HINs never contain I, O, or Q.
const OCR_CONFUSIONS: Array<[RegExp, string]> = [
  [/I/g, '1'],
  [/O/g, '0'],
  [/Q/g, '0'],
]

export function extractHINCandidate(text: string): string {
  const normalized = OCR_CONFUSIONS.reduce((s, [re, rep]) => s.replace(re, rep), text.toUpperCase())
  return normalized.match(HIN_TOKEN)?.[0] ?? ''
}

interface HinScannerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called with the OCR-suggested HIN; the user confirms/edits before use. */
  onHIN: (hin: string) => void
}

/**
 * Client-side OCR for the HIN plate photo: camera capture (mobile) or file
 * browse, Tesseract.js runs entirely in the browser (worker + language data
 * are lazy-loaded from the CDN on first use), and the recognized 12-14 char
 * token is offered as an editable suggestion — never trusted blindly.
 *
 * TODO: engraved metal plates are Tesseract's weak spot. If accuracy on real
 * plates proves poor, upgrade to a server-side vision provider (config-gated,
 * simulation mode when unset — same pattern as Twilio/Meta) without changing
 * this component's contract.
 */
export function HinScanner({ open, onOpenChange, onHIN }: HinScannerProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [photoUrl, setPhotoUrl] = useState<string | null>(null)
  const [status, setStatus] = useState<'idle' | 'running' | 'done' | 'error'>('idle')
  const [progress, setProgress] = useState(0)
  const [candidate, setCandidate] = useState('')
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    if (photoUrl) URL.revokeObjectURL(photoUrl)
    setPhotoUrl(null)
    setStatus('idle')
    setProgress(0)
    setCandidate('')
    setError(null)
  }

  // Reset on close via the handler (not an effect) — content unmounts anyway,
  // but the object URL and state live on this (persistent) component.
  const handleOpenChange = (next: boolean) => {
    if (!next) reset()
    onOpenChange(next)
  }

  const pickPhoto = async (file: File | undefined) => {
    if (!file) return
    if (photoUrl) URL.revokeObjectURL(photoUrl)
    setPhotoUrl(URL.createObjectURL(file))
    setCandidate('')
    setError(null)
    setStatus('running')
    setProgress(0)

    try {
      // Lazy import: the OCR engine (~WASM) only loads when someone scans.
      const { createWorker } = await import('tesseract.js')
      const worker = await createWorker('eng', 1, {
        logger: (m) => {
          if (m.status === 'recognizing text') setProgress(m.progress)
        },
      })
      await worker.setParameters({
        // HIN plates only ever carry uppercase letters and digits.
        tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789',
      })
      const { data } = await worker.recognize(file)
      await worker.terminate()

      const found = extractHINCandidate(data.text ?? '')
      setCandidate(found)
      setStatus('done')
      if (!found) setError('No HIN-like code found — type it in below or retake the photo.')
    } catch {
      setStatus('error')
      setError(
        'Photo recognition failed to load (it fetches from a CDN). Type the HIN instead or retry.',
      )
    }
  }

  const confirm = () => {
    if (candidate.trim()) onHIN(candidate.trim())
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-5 w-5" /> Scan HIN
          </DialogTitle>
          <DialogDescription>
            Photograph the hull identification number plate (usually on the transom's starboard
            corner). The reading is a suggestion — edit before using.
          </DialogDescription>
        </DialogHeader>

        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            void pickPhoto(e.target.files?.[0])
            if (inputRef.current) inputRef.current.value = ''
          }}
        />

        {photoUrl ? (
          <div className="relative overflow-hidden rounded-md border">
            <img src={photoUrl} alt="HIN plate" className="max-h-48 w-full object-contain" />
            {status === 'running' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/70">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="text-xs">Reading plate… {Math.round(progress * 100)}%</span>
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex h-32 w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed text-muted-foreground transition-colors hover:border-primary hover:text-primary"
          >
            <Camera className="h-7 w-7" />
            <span className="text-sm">Take / choose a photo of the plate</span>
          </button>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}

        {status === 'done' && (
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="hin-candidate">
              Recognized HIN
            </label>
            <div className="flex gap-2">
              <Input
                id="hin-candidate"
                value={candidate}
                onChange={(e) => setCandidate(e.target.value.toUpperCase())}
                placeholder="e.g. YAM4B212J423"
                maxLength={20}
                className="font-mono tracking-wider"
              />
              <Button type="button" onClick={confirm} disabled={!candidate.trim()}>
                Use
              </Button>
            </div>
          </div>
        )}

        {photoUrl && status !== 'running' && (
          <Button type="button" variant="outline" onClick={() => inputRef.current?.click()}>
            <Camera className="mr-2 h-4 w-4" /> Retake
          </Button>
        )}
      </DialogContent>
    </Dialog>
  )
}
